import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Creates a linked Level 5 account for a customer, vendor, or bank
 * @param {string} companyId - The company ID
 * @param {string} entityType - Type of entity ('customer', 'vendor', 'bank')
 * @param {string} entityId - The entity's document ID
 * @param {string} entityName - The entity's name
 * @param {string} accountCode - The account code for this entity
 * @param {string} parentAccountId - The parent account ID (Level 4 account)
 * @param {number} openingBalance - Opening balance for the account
 * @param {string} userId - The user creating the account
 * @returns {Promise<string>} - The created account ID
 */
export const createLinkedAccount = async (
  companyId,
  entityType,
  entityId,
  entityName,
  accountCode,
  parentAccountId,
  openingBalance = 0,
  userId
) => {
  try {
    // Get parent account to inherit properties
    const parentAccountsRef = collection(db, 'companies', companyId, 'accounts');
    const parentQuery = query(parentAccountsRef, where('__name__', '==', parentAccountId));
    const parentSnapshot = await getDocs(parentQuery);

    if (parentSnapshot.empty) {
      throw new Error('Parent account not found');
    }

    const parentAccount = {
      id: parentSnapshot.docs[0].id,
      ...parentSnapshot.docs[0].data()
    };

    // Create Level 5 account (postable/detail account)
    const accountData = {
      code: accountCode,
      name: entityName,
      level: 5,
      parentId: parentAccountId,
      path: [...(parentAccount.path || []), parentAccountId],
      accountType: parentAccount.accountType,
      balanceType: parentAccount.balanceType,
      openingBalance: openingBalance,
      currentBalance: openingBalance,
      linkedEntityType: entityType,
      linkedEntityId: entityId,
      isActive: true,
      description: `Linked ${entityType} account for ${entityName}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: userId
    };

    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const docRef = await addDoc(accountsRef, accountData);

    return docRef.id;
  } catch (error) {
    console.error('Error creating linked account:', error);
    throw error;
  }
};

/**
 * Finds the appropriate parent account for a given entity type
 * @param {string} companyId - The company ID
 * @param {string} entityType - Type of entity ('customer', 'vendor', 'bank')
 * @returns {Promise<Object|null>} - The parent account or null if not found
 */
export const findParentAccountForEntity = async (companyId, entityType) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');

    // Define the parent account codes based on entity type (Level 4 accounts).
    // Codes match src/data/defaultChartOfAccounts.js LINKED_ACCOUNT_PARENTS.
    const parentCodes = {
      customer: ['11201'], // Receivable Customers
      vendor: ['21101'],   // Payable Vendors
      bank: ['11102'],     // Bank Accounts
      staff: ['11204']     // Staff Accounts (Salary Staff master)
    };

    const searchCodes = parentCodes[entityType] || [];

    // Search for parent account at Level 4 by code
    for (const code of searchCodes) {
      const q = query(
        accountsRef,
        where('level', '==', 4),
        where('code', '==', code)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        };
      }
    }

    // If the L4 parent is missing, the caller should run Repair COA — that
    // path (ensureParentAccountExists in coaInitializer) is the single source
    // of truth for creating missing seed parents.
    return null;
  } catch (error) {
    console.error('Error finding parent account:', error);
    return null;
  }
};

/**
 * Generates the next available account code under a parent account
 * Follows the pattern: L1(1) → L2(2) → L3(3) → L4(5) → L5(8)
 * @param {string} companyId - The company ID
 * @param {string} parentAccountId - The parent account ID
 * @param {string} parentAccountCode - The parent account code
 * @param {number} parentLevel - The parent account level
 * @returns {Promise<string>} - The next available account code
 */
export const generateAccountCode = async (companyId, parentAccountId, parentAccountCode, parentLevel) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const q = query(
      accountsRef,
      where('parentId', '==', parentAccountId)
    );
    const snapshot = await getDocs(q);

    // Determine suffix length based on level
    // L1→L2: add 1 digit (1→10)
    // L2→L3: add 1 digit (10→100)
    // L3→L4: add 2 digits (100→10001)
    // L4→L5: add 3 digits (10001→10001001)
    let suffixLength;
    switch (parentLevel) {
      case 1: suffixLength = 1; break; // L2 = 2 digits total
      case 2: suffixLength = 1; break; // L3 = 3 digits total
      case 3: suffixLength = 2; break; // L4 = 5 digits total
      case 4: suffixLength = 3; break; // L5 = 8 digits total
      default: suffixLength = 3;
    }

    if (snapshot.empty) {
      return `${parentAccountCode}${'0'.repeat(suffixLength - 1)}1`;
    }

    // Find the highest code number
    const codes = snapshot.docs.map(doc => doc.data().code);
    const numbers = codes
      .filter(code => code.startsWith(parentAccountCode))
      .map(code => {
        const suffix = code.replace(parentAccountCode, '');
        return parseInt(suffix) || 0;
      });

    const maxNumber = Math.max(...numbers, 0);
    const nextNumber = maxNumber + 1;

    return `${parentAccountCode}${String(nextNumber).padStart(suffixLength, '0')}`;
  } catch (error) {
    console.error('Error generating account code:', error);
    return `${parentAccountCode}${'0'.repeat(3 - 1)}1`;
  }
};

/**
 * Gets the account linked to an entity
 * @param {string} companyId - The company ID
 * @param {string} entityType - Type of entity ('customer', 'vendor', 'bank')
 * @param {string} entityId - The entity's document ID
 * @returns {Promise<Object|null>} - The linked account or null if not found
 */
export const getLinkedAccount = async (companyId, entityType, entityId) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const q = query(
      accountsRef,
      where('linkedEntityType', '==', entityType),
      where('linkedEntityId', '==', entityId)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting linked account:', error);
    return null;
  }
};
